/**
 * Sends a user whose session is gone to the login form.
 *
 * The session cookie outlives the token inside it: the JWT is signed for 7
 * days, the cookie is set for 30, and a password change or ownership transfer
 * revokes the token early. In that gap the router guard still sees a logged-in
 * user and lets them onto /courses, where every API call answers 401 and
 * nothing reacted — `fetchCourses` caught the error, logged it, and the page
 * rendered empty. PostHog counted 56 of those from 24 users in one week before
 * 4xx reporting was switched off, so the symptom is now invisible there and
 * this is the fix rather than the report.
 *
 * Same shape as installApiErrorReporting: act on the way past and rethrow
 * untouched, so every existing `catch` keeps working.
 *
 * Whether to redirect is decided by the route the user is on, not by the URL
 * that failed. The reviewer pages (/review, /view, /public-review,
 * /course-review) are public routes that answer an auth failure in their own
 * UI — "Login Required", with the invite link kept — and must not be bounced.
 * Only a route the router marks `meta.requiresAuth` sends the user to /login,
 * with the page they were on as the return path Login.vue already honours.
 */

import { useUserStore } from "../stores/userStore";
import { rememberAuthRedirect } from "./authRedirect";

/**
 * @param {Object} axios - The axios instance to instrument.
 * @param {Object} router - The app router. Read at response time, never here.
 */
export function installSessionExpiryRedirect(axios, router) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // A call site opts out with `skipAuthRedirect: true` on its request
      // config, as the Stripe checkout-session lookup does: it proves its own
      // access with a header, so its 401 is about the proof, not the session.
      if (
        error?.response?.status === 401 &&
        !error.config?.skipAuthRedirect
      ) {
        const route = router.currentRoute.value;
        const userStore = useUserStore();
        // A page's requests fail together. The first clears the session; the
        // rest find it already gone and let that navigation finish.
        if (route.meta.requiresAuth && userStore.user?.email) {
          userStore.logout();
          // The caller's own catch runs after this and may navigate too —
          // Review.vue answers a 401 with a plain push to /login, which would
          // drop the query. Login.vue reads the router's remembered path
          // first, so the return path survives either way.
          rememberAuthRedirect(route.fullPath);
          router.replace({
            path: "/login",
            query: { redirect: route.fullPath },
          });
        }
      }

      return Promise.reject(error);
    },
  );
}
